import { formatDistanceToNow, type Locale } from "date-fns";
import { Bell, X } from "lucide-react";
import { useEffect, useRef, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  useClearAllNotifications,
  useDeleteNotification,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from "@/hooks/api/useNotifications";
import { useAuth } from "@/hooks/useAuth";
import { DateFormatVariant, formatDate, resolveDateLocale } from "@/lib/dateFormat";
import { notificationLink } from "@/lib/notificationLink";
import { cn } from "@/lib/utils";
import { PROJECT_REPORT_TYPE_KEYS, type ProjectReportTypeKey } from "@/types/api/diaryReports";
import type { NotificationDto, NotificationTypeKey } from "@/types/api/notifications";

function asParamString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function isProjectReportTypeKey(value: string): value is ProjectReportTypeKey {
  return (PROJECT_REPORT_TYPE_KEYS as readonly string[]).includes(value);
}

function notificationTitle(
  t: (key: string, options?: Record<string, unknown>) => string,
  notification: NotificationDto,
): string {
  const typeKey = notification.typeKey;
  switch (typeKey) {
    case "projectReport.ready":
      return t("notifications.types.projectReport.ready");
    case "projectReport.failed":
      return t("notifications.types.projectReport.failed");
    default: {
      const _exhaustive: never = typeKey;
      return _exhaustive;
    }
  }
}

/** Report model + period for the second line; null for legacy notifications. */
function notificationDetail(
  t: (key: string, options?: Record<string, unknown>) => string,
  notification: NotificationDto,
  dateLocale: Locale,
): string | null {
  const params = notification.params;
  const modelName = asParamString(params.modelName);
  const dateFrom = asParamString(params.dateFrom);
  const dateTo = asParamString(params.dateTo);
  const reportType = asParamString(params.reportType);

  const typeLabel =
    reportType && isProjectReportTypeKey(reportType)
      ? t(`diaryReports.reportTypes.${reportType}.name`)
      : null;
  const name = modelName ?? typeLabel;
  if (!name) return null;

  if (dateFrom && dateTo) {
    const fromLabel = formatDate(dateFrom, dateLocale, DateFormatVariant.Date);
    const toLabel = formatDate(dateTo, dateLocale, DateFormatVariant.Date);
    const period = dateFrom === dateTo ? fromLabel : `${fromLabel} – ${toLabel}`;
    return `${name} · ${period}`;
  }

  return name;
}

function isNotificationTypeKey(value: string): value is NotificationTypeKey {
  return value === "projectReport.ready" || value === "projectReport.failed";
}

export function NotificationsBell(): ReactElement | null {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const enabled = Boolean(user?.organization?.id);
  const notificationsQuery = useNotifications(enabled);
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const deleteOne = useDeleteNotification();
  const clearAll = useClearAllNotifications();
  const previousUnreadRef = useRef<number | null>(null);
  const dateLocale = resolveDateLocale(i18n.language);

  const items = notificationsQuery.data?.items ?? [];
  const unreadCount = notificationsQuery.data?.unreadCount ?? 0;
  const refetchNotifications = notificationsQuery.refetch;

  useEffect(() => {
    if (!open) return;
    void refetchNotifications();
  }, [open, refetchNotifications]);

  useEffect(() => {
    if (!enabled || notificationsQuery.data == null) return;
    const previous = previousUnreadRef.current;
    previousUnreadRef.current = unreadCount;
    if (previous == null || unreadCount <= previous) return;

    const newestUnread = items.find((item) => item.readAt == null);
    if (!newestUnread || !isNotificationTypeKey(newestUnread.typeKey)) return;
    const detail = notificationDetail(t, newestUnread, dateLocale);
    toast.message(notificationTitle(t, newestUnread), {
      ...(detail ? { description: detail } : {}),
    });
  }, [dateLocale, enabled, items, notificationsQuery.data, t, unreadCount]);

  if (!enabled) return null;

  const label = t("notifications.title");
  const badgeLabel = unreadCount > 99 ? "99+" : unreadCount > 0 ? String(unreadCount) : null;
  const busy = markAllRead.isPending || deleteOne.isPending || clearAll.isPending;

  function handleOpen(notification: NotificationDto): void {
    if (notification.readAt == null) {
      markRead.mutate(notification.id);
    }
    const href = notificationLink(notification);
    if (href) {
      setOpen(false);
      navigate(href);
    }
  }

  function handleDismiss(notification: NotificationDto): void {
    deleteOne.mutate(notification.id);
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="relative size-9 shrink-0 lg:size-12"
              aria-label={label}
            >
              <Bell className="size-[20px]" aria-hidden />
              {badgeLabel ? (
                <span className="absolute top-2 right-2 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold text-destructive-foreground">
                  {badgeLabel}
                </span>
              ) : null}
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom">{label}</TooltipContent>
      </Tooltip>

      <DropdownMenuContent
        align="end"
        className="w-[22rem] gap-0 overflow-hidden p-0"
        // Keep focus in the panel when a row is removed (dismiss).
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        <div className="space-y-1.5 px-3 py-2">
          <DropdownMenuLabel className="p-0">{label}</DropdownMenuLabel>
          {items.length > 0 ? (
            <div className="flex flex-wrap items-center gap-x-1 gap-y-1">
              {unreadCount > 0 ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  disabled={busy}
                  onClick={() => markAllRead.mutate()}
                >
                  {t("notifications.markAllRead")}
                </Button>
              ) : null}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs text-muted-foreground"
                disabled={busy}
                onClick={() => clearAll.mutate()}
              >
                {t("notifications.clearAll")}
              </Button>
            </div>
          ) : null}
        </div>
        <DropdownMenuSeparator className="my-0" />
        <div className="max-h-80 overflow-y-auto py-1">
          {items.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              {t("notifications.empty")}
            </p>
          ) : (
            items.map((item) => {
              const unread = item.readAt == null;
              const detail = notificationDetail(t, item, dateLocale);
              return (
                <div
                  key={item.id}
                  className={cn("flex items-start gap-2 px-3 py-2.5", unread && "bg-accent/40")}
                >
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 items-start gap-2 rounded-md text-left outline-none hover:opacity-90 focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={() => handleOpen(item)}
                  >
                    <span
                      className={cn(
                        "mt-1.5 size-1.5 shrink-0 rounded-full",
                        unread ? "bg-primary" : "bg-transparent",
                      )}
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1 space-y-0.5">
                      <span className="block text-sm leading-snug text-foreground">
                        {notificationTitle(t, item)}
                      </span>
                      {detail ? (
                        <span className="block truncate text-xs leading-snug text-muted-foreground">
                          {detail}
                        </span>
                      ) : null}
                      <span className="block text-xs text-muted-foreground/80">
                        {formatDistanceToNow(new Date(item.createdAt), {
                          addSuffix: true,
                          locale: dateLocale,
                        })}
                      </span>
                    </span>
                  </button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-7 shrink-0 text-muted-foreground hover:text-foreground"
                    aria-label={t("notifications.dismiss")}
                    disabled={busy}
                    onClick={() => handleDismiss(item)}
                  >
                    <X className="size-3.5" aria-hidden />
                  </Button>
                </div>
              );
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
